import React, { useState } from 'react';
import { LuClipboard } from 'react-icons/lu';

import { GlobalAccount, GUID } from '@ndvr/customer-api';
import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { useCopyGuid } from '@/components/CopyGuidComponents';
import { ChangeAccountOwnerDrawer } from 'views/household/AccountGroupTab/ChangeAccountOwnerDrawer';
import { ChangeLinkedCustodyAccountDrawer } from 'views/household/AccountGroupTab/ChangeLinkedCustodyAccountDrawer';
import { CloseAccountValidationModal } from 'views/household/AccountGroupTab/CloseAccountValidationModal';
import { AccountTradeStatusModal } from 'views/portfolio/PortfolioDetails/AccountTradeStatusModal';
import {
    FlyoutItem,
    FlyoutMenu,
    IconClose,
    IconPencilOutline,
    IconPersons,
    IconTradingMoney,
    IconTrash,
} from 'widgets';

export const AccountContextMenu = ({
    householdGUID,
    account,
    onDelete,
}: {
    householdGUID: GUID<GlobalAccount>;
    account: Account;
    onDelete?: (account: Account) => void;
}) => {
    const { copyGuid, copyGuidLabel } = useCopyGuid(account.accountGUID);
    const [showTradeStatusModal, setShowTradeStatusModal] = useState(false);
    const [showChangeCustodyAccountDrawer, setShowChangeCustodyAccountDrawer] = useState(false);
    const [showChangeAccountOwnerDrawer, setShowChangeAccountOwnerDrawer] = useState(false);
    const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);

    const showLiveAccountActions = account.lifecycleStatus === LifecycleStatus.REAL;
    const showDeleteAction = onDelete && account.lifecycleStatus === LifecycleStatus.DRAFT;

    if (!showLiveAccountActions && !showDeleteAction) {
        return null;
    }

    return (
        <>
            <FlyoutMenu>
                {showLiveAccountActions && (
                    <>
                        <FlyoutItem icon={<LuClipboard />} onClick={copyGuid}>
                            {copyGuidLabel}
                        </FlyoutItem>
                        <FlyoutItem
                            icon={<IconTradingMoney size="micro" />}
                            onClick={() => setShowTradeStatusModal(true)}
                        >
                            {account.tradingSuspendedAt ? 'Enable' : 'Suspend'} trading
                        </FlyoutItem>
                        <FlyoutItem
                            icon={<IconPencilOutline size="micro" />}
                            onClick={() => setShowChangeCustodyAccountDrawer(true)}
                        >
                            Change linked custody account
                        </FlyoutItem>
                        <FlyoutItem
                            icon={<IconPersons size="micro" />}
                            onClick={() => setShowChangeAccountOwnerDrawer(true)}
                        >
                            Change account owner
                        </FlyoutItem>
                        <FlyoutItem icon={<IconClose size="micro" />} onClick={() => setShowCloseAccountModal(true)}>
                            Close account
                        </FlyoutItem>
                    </>
                )}
                {showDeleteAction && (
                    <FlyoutItem icon={<IconTrash size="micro" />} onClick={() => onDelete(account)}>
                        Delete account
                    </FlyoutItem>
                )}
            </FlyoutMenu>
            {showTradeStatusModal && (
                <AccountTradeStatusModal
                    householdGUID={householdGUID}
                    account={account}
                    onClose={() => setShowTradeStatusModal(false)}
                />
            )}
            {showChangeCustodyAccountDrawer && (
                <ChangeLinkedCustodyAccountDrawer
                    householdGUID={householdGUID}
                    account={account}
                    onClose={() => setShowChangeCustodyAccountDrawer(false)}
                />
            )}
            {showChangeAccountOwnerDrawer && (
                <ChangeAccountOwnerDrawer
                    householdGUID={householdGUID}
                    account={account}
                    onClose={() => setShowChangeAccountOwnerDrawer(false)}
                />
            )}
            {showCloseAccountModal && (
                <CloseAccountValidationModal
                    householdGUID={householdGUID}
                    account={account}
                    onClose={() => setShowCloseAccountModal(false)}
                />
            )}
        </>
    );
};
